import { Component, OnInit, Input, Inject, LOCALE_ID } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { LocalStorageService } from 'src/services/local-storage/local-storage.service';
import { Plan } from 'src/classes/plan';
import { UserService } from 'src/services/user/user.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-payment-method-form',
  templateUrl: './payment-method-form.component.html',
  styleUrls: ['./payment-method-form.component.scss']
})
export class PaymentMethodFormComponent implements OnInit {

  @Input() selectedPlan: Plan;
  @Input() isNewPlan: boolean;

  public showPaymentProcessorElement = false;
  public form: FormGroup;
  public sending = false;
  public success = false;
  public formError: string;

  constructor(
    private formBuilder: FormBuilder,
    private localStorage: LocalStorageService,
    @Inject(LOCALE_ID) private locale: string,
    private userService: UserService,
    private router: Router,
  ) { }

  getFormInput(name: string) {
    if (!this.form) {
      return null;
    }
    return this.form.get(name);
  }

  get company() { return this.getFormInput('company'); }
  get personType() { return this.getFormInput('personType'); }
  get taxID() { return this.getFormInput('taxID'); }
  get stateInscription() { return this.getFormInput('stateInscription'); }
  get billingEmail() { return this.getFormInput('billingEmail'); }
  get daysBeforeDue() { return this.getFormInput('daysBeforeDue'); }
  get zipCode() { return this.getFormInput('zipCode'); }
  get state() { return this.getFormInput('state'); }
  get city() { return this.getFormInput('city'); }
  get neighborhood() { return this.getFormInput('neighborhood'); }
  get address1() { return this.getFormInput('address1'); }
  get address2() { return this.getFormInput('address2'); }
  get address3() { return this.getFormInput('address3'); }


  ngOnInit() {
    this.form = this.formBuilder.group({
      'company': ['', Validators.required],
      'personType': ['1', Validators.required],
      'taxID': ['', Validators.required],
      'stateInscription': [''],
      'billingEmail': ['', Validators.required],
      'daysBeforeDue': ['15', Validators.required],
      'zipCode': ['', Validators.required],
      'state': ['', Validators.required],
      'city': ['', Validators.required],
      'neighborhood': ['', Validators.required],
      'address1': ['', Validators.required],
      'address2': ['', Validators.required],
      'address3': ['']
    });
    this.loadFormData();
    this.initPaypal();
  }

  async loadFormData() {
    const user = await this.userService.get();
    const paramLookup = {
      'company': 'company',
      'billingMail': 'billingEmail',
      'daysBeforeDue': 'daysBeforeDue',
      'neighborhood': 'neighborhood',
      'address': 'address1',
      'addressNumber': 'address2',
      'addressComplement': 'address3',
      'zipCode': 'zipCode',
      'federalUnit': 'state',
      'city': 'city',
      'stateRegister': 'stateInscription',
    };

    const taxID = user.paramValues.taxID;
    if (taxID) {
      // Fill the taxId (CPF/CNPJ)
      this.taxID.setValue(taxID);

      // Set the person type based on the tax Id
      this.personType.setValue(taxID.length === 11 ? '0' : '1');
    }

    // Fill the inputs (set their values) according to the lookup
    for (let key in user.paramValues) {
      if (!this[paramLookup[key]]) {
        continue;
      }
      this[paramLookup[key]].setValue(user.paramValues[key]);
    }
  }

  onApprove(subscriptionId) {
    this.localStorage.setItem('last-subscription-id', subscriptionId);
    throw new Error('Unimplemented');
  }

  initPaypal() {
    if (!window['paypal']) {
      throw new Error('Paypal module has not been loaded');
    }
    window['paypal'].Buttons({
      style: {
          shape: 'rect',
          color: 'blue',
          layout: 'vertical',
          label: 'subscribe',
      },
      createSubscription: (_, actions) => {
        return actions.subscription.create({ 'plan_id': this.selectedPlan.id });
      },
      onApprove: (data) => {
        this.onApprove(data.subscriptionID);
      }
    }).render('#paymentProcessorElement');
  }

  isEnglishLocale() {
    return !(typeof this.locale === 'string' && this.locale.toLowerCase().substring(0, 2) === 'pt');
  }

  getSelectedPlanPrice(plan: Plan) {
    if (!plan) {
      return '...';
    }
    return plan.getPlanPrice(this.isEnglishLocale());
  }

  getMonthlyDeliveryCount(plan) {
    if (!plan) {
      return '...';
    }
    if (plan.deliveries === -1) {
      if (this.isEnglishLocale()) {
        return 'Unlimited';
      } else {
        return 'Ilimitado';
      }
    }
    if (typeof plan.deliveries === 'number') {
      return Intl.NumberFormat((this.isEnglishLocale()) ? 'en-US' : 'pt-BR', { maximumFractionDigits: 0 }).format(plan.deliveries)
    } else {
      return plan.deliveries;
    }
  }

  async onFinishClick() {
    if (!this.selectedPlan) {
      return;
    }

    if (this.form.invalid) {
      let errorMessage = '';
      this.form.markAsTouched();
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        const controlErrors = control.errors;
        if (controlErrors !== null) {
          control.markAllAsTouched();
          control.markAsDirty();
          if (this.isEnglishLocale()) {
            errorMessage += `The field '${key}' is invalid\n`;
          } else {
            const ptKey = {
              'company': 'Empresa', 'taxID': 'CPF/CNPJ',
              'billingEmail': 'Email de CobranÃ§a',
              'zipCode': 'CEP', 'state': 'Estado',
              'city': 'Cidade', 'neighborhood': 'Bairro',
              'address1': 'Rua', 'address2': 'NÃºmero'
            };
            errorMessage += `Campo '${ptKey[key] ? ptKey[key] : key}' estÃ¡ invÃ¡lido\n`;
          }
        }
      });
      if (this.isEnglishLocale()) {
        this.formError = 'There are errors in your form:\n' + errorMessage;
      } else {
        this.formError = 'Existem erros no seu formulÃ¡rio:\n' + errorMessage;
      }
      return;
    }
    if (!this.billingEmail.value.toString().includes('@') || !this.billingEmail.value.toString().includes('.')) {
      if (this.isEnglishLocale()) {
        this.formError = 'Billing email is invalid';
      } else {
        this.formError = 'Email de cobrança está inválido';
      }
      return;
    }

    this.sending = true;
    this.success = false;
    this.formError = '';
    const idPlan = this.selectedPlan.id.toString();
    const paymentType = 'Billing';
    const company = this.company.value;
    const taxID = this.taxID.value;
    const stateRegister = this.stateInscription.value;
    const billingMail = this.billingEmail.value;
    const zipCode = this.zipCode.value;
    const federalUnit = this.state.value;
    const city = this.city.value;
    const neighborhood = this.neighborhood.value;
    const address = this.address1.value;
    const addressNumber = this.address2.value;
    const addressComplement = this.address3.value;
    const subscriptionId = 'none';
    const daysBeforeDue = parseInt(this.daysBeforeDue.value, 10);

    if (![5, 10, 15].includes(daysBeforeDue)) {
      if (this.isEnglishLocale()) {
        this.formError = 'A opção de dias antes do envio do pagamento é inválido';
      } else {
        this.formError = 'The option for days before the payment is due is invalid';
      }
      return;
    }

    const personType = this.personType.value === '0' ? 'F' : 'J'

    try {
      await Promise.all([
        new Promise(resolve => setTimeout(resolve, 500)),
        this.userService.updatePaymentData(
          idPlan,
          paymentType,
          company,
          taxID,
          stateRegister,
          billingMail,
          zipCode,
          federalUnit,
          city,
          neighborhood,
          address,
          addressNumber,
          addressComplement,
          subscriptionId,
          daysBeforeDue,
          personType
        )
      ]);
      this.form.markAsPristine();
      this.success = true;
      setTimeout(() => {
        this.router.navigate(['/billing']);
      }, 500);
    } catch (err) {
      let prefix = '';
      if (err.trace && err.trace.includes('MercadoPago')) {
        prefix += 'MercadoPago: ';
      }
      this.formError = prefix + err.message;
    }

    this.sending = false;
  }

}
